import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../helpers/axiosInstance";
import { errorMessage } from "../../helpers/format";
import FlipLoader from "../fliploader/FlipLoader";
import "./ui.css";

/**
 * Fetch something from the server and keep it with its loading and error
 * state. `select` picks what to keep out of the response body; `skip` holds
 * off until whatever the URL needs is known.
 */
export const useFetch = (url, { select = (body) => body, skip = false } = {}) => {
  const [state, setState] = useState({ data: undefined, error: null, loading: !skip });
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (skip || !url) {
      setState({ data: undefined, error: null, loading: false });
      return undefined;
    }
    let live = true;
    setState((previous) => ({ ...previous, loading: true, error: null }));
    axiosInstance
      .get(url)
      .then((response) => live && setState({ data: select(response.data), error: null, loading: false }))
      .catch((error) => live && setState({ data: undefined, error: errorMessage(error), loading: false }));
    return () => {
      live = false;
    };
    // `select` is a pure picker; refetching when its identity changes would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, skip, version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  return { ...state, reload };
};

/** A page: title, an optional back arrow, actions on the right. */
export const Page = ({ title, subtitle, back = false, actions, children }) => {
  const navigate = useNavigate();
  return (
    <div className="sf-page">
      <header className="sf-page-header">
        {back ? (
          <button className="sf-page-back" aria-label="Back" onClick={() => navigate(-1)}>
            ←
          </button>
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 className="sf-page-title">{title}</h3>
          {subtitle ? <p className="sf-page-subtitle">{subtitle}</p> : null}
        </div>
        {actions ? <div className="sf-page-actions">{actions}</div> : null}
      </header>
      {children}
    </div>
  );
};

export const SectionTitle = ({ title, action }) => (
  <div className="sf-section-title">
    <h4>{title}</h4>
    {action || null}
  </div>
);

export const Card = ({ children, flush = false, style }) => (
  <div className={flush ? "sf-card sf-card-flush" : "sf-card"} style={style}>
    {children}
  </div>
);

export const Button = ({ variant = "secondary", block = false, busy = false, children, ...props }) => (
  <button
    className={["sf-btn", `sf-btn-${variant}`, block ? "sf-btn-block" : ""].join(" ")}
    disabled={busy || props.disabled}
    {...props}
  >
    {busy ? "Working…" : children}
  </button>
);

/** A small status label. tone: good | warn | bad | strong | (none). */
export const Pill = ({ tone, children }) => <span className={tone ? `sf-pill sf-pill-${tone}` : "sf-pill"}>{children}</span>;

export const Banner = ({ tone, title, children }) => (
  <div className={tone ? `sf-banner sf-banner-${tone}` : "sf-banner"}>
    {title ? <p className="sf-banner-title">{title}</p> : null}
    {children ? <div className="sf-muted">{children}</div> : null}
  </div>
);

export const Empty = ({ title, children }) => (
  <div className="sf-empty">
    <h4>{title}</h4>
    {children ? <p>{children}</p> : null}
  </div>
);

export const Loading = () => (
  <div className="sf-loading">
    <FlipLoader />
  </div>
);

/** Loading, a failure with a retry, or the content. */
export const Async = ({ state, children }) => {
  if (state.loading && state.data === undefined) return <Loading />;
  if (state.error) {
    return (
      <Empty title="Could not load this">
        {state.error}{" "}
        <Button variant="text" onClick={state.reload}>
          Try again
        </Button>
      </Empty>
    );
  }
  return children(state.data);
};

export const Field = ({ label, help, error, children }) => (
  <div className="sf-field">
    {label ? <label>{label}</label> : null}
    {children}
    {error ? <span className="sf-field-error">{error}</span> : help ? <span className="sf-field-help">{help}</span> : null}
  </div>
);

/** A modal. Closes on Escape and on a click outside it. */
export const Dialog = ({ title, onClose, children, actions }) => {
  useEffect(() => {
    const onKey = (event) => event.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="sf-overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose?.()}>
      <div className="sf-dialog" role="dialog" aria-modal="true" aria-label={title}>
        <h3>{title}</h3>
        {children}
        {actions ? <div className="sf-dialog-actions">{actions}</div> : null}
      </div>
    </div>
  );
};

/** Are you sure? — with the consequence spelled out. */
export const Confirm = ({ title, children, confirmLabel = "Confirm", danger = false, busy = false, onConfirm, onCancel }) => (
  <Dialog
    title={title}
    onClose={busy ? undefined : onCancel}
    actions={
      <>
        <Button variant="text" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant={danger ? "danger" : "primary"} onClick={onConfirm} busy={busy}>
          {confirmLabel}
        </Button>
      </>
    }
  >
    {typeof children === "string" ? <p className="sf-muted">{children}</p> : children}
  </Dialog>
);

/**
 * Run a request that changes something, with a busy flag and a toast either
 * way. Resolves to the response body, or undefined when it failed.
 */
export const useAction = () => {
  const [busy, setBusy] = useState(false);
  const run = useCallback(async (request, { done, failed } = {}) => {
    setBusy(true);
    try {
      const response = await request();
      if (done) toast(typeof done === "function" ? done(response.data) : done);
      return response.data ?? true;
    } catch (error) {
      toast(errorMessage(error, failed || "Something went wrong. Nothing was changed."));
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, run };
};

/**
 * Fetch a file the server builds — a receipt, an invoice, a statement — and
 * save it, so every printable record downloads the same way.
 */
export const DownloadButton = ({ url, fileName, label = "Download", variant = "secondary", block = false }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const response = await axiosInstance.get(url, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(response.data);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 10000);
    } catch (error) {
      // A blob error body has to be read before its message can be shown.
      let message = "Could not download it. Try again.";
      try {
        const text = await error?.response?.data?.text?.();
        message = JSON.parse(text)?.message || message;
      } catch (_) {}
      toast(message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant={variant} block={block} onClick={download} disabled={busy}>
      {busy ? "Preparing…" : `⤓ ${label}`}
    </Button>
  );
};
